import { useRef, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Brand from "@/components/Brand";
import { ArrowLeft, MailCheck } from "lucide-react";
import { useMerchantAuth } from "@/lib/auth";
import { extractErr } from "@/lib/api";
import LoginPasswordInput, { passwordFromForm } from "@/components/LoginPasswordInput";

export default function MerchantLogin() {
  const nav = useNavigate();
  const { login, verifyLoginOtp, user, loading: authLoading } = useMerchantAuth();
  const [step, setStep] = useState("creds"); // creds | otp
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const passwordHold = useRef("");

  const submitCreds = async (e) => {
    e.preventDefault();
    const password = passwordFromForm(e.currentTarget);
    passwordHold.current = password;
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res?.otp_required) {
        setStep("otp");
        toast.success(`We emailed a 6-digit code to ${email}`);
      } else {
        passwordHold.current = "";
        nav("/admin");
      }
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setLoading(false);
    }
  };

  const submitOtp = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await verifyLoginOtp(email, code.trim());
      toast.success("Welcome back");
      passwordHold.current = "";
      nav("/admin");
    } catch (err) {
      toast.error(extractErr(err));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      await login(email, passwordHold.current);
      toast.success("New code sent");
    } catch (err) {
      toast.error(extractErr(err));
    }
  };

  if (authLoading) {
    return <div className="min-h-screen bg-slate-950 flex items-center justify-center text-slate-400">Loading…</div>;
  }
  if (user) return <Navigate to="/admin" replace />;

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center gap-2 text-sm text-slate-400 hover:text-white mb-8" data-testid="login-back">
          <ArrowLeft className="w-4 h-4" /> Home
        </Link>
        <div className="mb-6">
          <Brand variant="dark" size="md" />
        </div>

        {step === "creds" ? (
          <>
            <h1 className="font-display text-3xl font-bold">Sign in to your store</h1>
            <p className="text-slate-400 text-sm mt-1">Merchant login</p>
            <form onSubmit={submitCreds} className="mt-8 space-y-4">
              <div>
                <Label className="text-slate-300">Email</Label>
                <Input required type="email" name="email" autoComplete="username" data-testid="login-email" className="bg-slate-900 border-slate-700 text-white mt-1" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>
              <div>
                <Label className="text-slate-300">Password</Label>
                <LoginPasswordInput testId="login-password" className="bg-slate-900 border-slate-700 text-white mt-1" />
              </div>
              <Button type="submit" disabled={loading} data-testid="login-submit" className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-slate-900 font-semibold rounded-full">
                {loading ? "Sending code…" : "Continue"}
              </Button>
              <div className="text-center">
                <Link to="/forgot-password" data-testid="login-forgot" className="text-sm text-slate-400 hover:text-slate-200">Forgot your password?</Link>
              </div>
            </form>
          </>
        ) : (
          <>
            <div className="w-11 h-11 rounded-xl bg-orange-500/15 border border-orange-400/40 flex items-center justify-center mb-3"><MailCheck className="w-5 h-5 text-orange-300" /></div>
            <h1 className="font-display text-3xl font-bold">Enter your code</h1>
            <p className="text-slate-400 text-sm mt-1">We sent a 6-digit code to <span className="text-slate-200">{email}</span></p>
            <form onSubmit={submitOtp} className="mt-8 space-y-4">
              <div>
                <Label className="text-slate-300">Verification code</Label>
                <Input required inputMode="numeric" maxLength={6} autoFocus data-testid="login-otp" placeholder="••••••" className="bg-slate-900 border-slate-700 text-white mt-1 text-center text-2xl tracking-[0.5em] font-mono-nums" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} />
              </div>
              <Button type="submit" disabled={loading || code.length < 6} data-testid="login-otp-submit" className="w-full h-11 bg-orange-500 hover:bg-orange-600 text-slate-900 font-semibold rounded-full">
                {loading ? "Verifying…" : "Verify & sign in"}
              </Button>
              <div className="flex items-center justify-between text-sm">
                <button type="button" onClick={() => { setStep("creds"); setCode(""); }} className="text-slate-400 hover:text-slate-200" data-testid="login-otp-back">Back</button>
                <button type="button" onClick={resend} className="text-orange-300 hover:text-orange-200" data-testid="login-otp-resend">Resend code</button>
              </div>
            </form>
          </>
        )}

        <div className="mt-6 text-sm text-slate-400 text-center">
          New here? <Link to="/signup" className="text-orange-300 hover:text-orange-200" data-testid="login-to-signup">Create a store</Link>
        </div>
      </div>
    </div>
  );
}
